/**
 * API functions for session management actions
 * (Away toggle, Checkout, Tags, etc.)
 */

import { apiCall, novaProxyHeaders } from './api';

/**
 * Toggle Away status for a session
 * Away status is stored as a persistent patient tag
 */
export async function toggleAwayStatus(
  sessionId: number,
  isAway: boolean,
  novaAccountId?: string | null
): Promise<void> {
  const tags = isAway ? [{ name: 'Away', value: 'true' }] : [];
  
  const response = await apiCall('/api/nova/_ah/api/admin/v3/persistent-patient-tags', {
    method: 'POST',
    headers: novaProxyHeaders(novaAccountId),
    body: JSON.stringify({
      sessionId,
      diaperId: null,
      tagger: null,
      tags,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to toggle away status: ${error}`);
  }
}

/**
 * Checkout/Close a session
 */
export async function checkoutSession(sessionId: number, novaAccountId?: string | null): Promise<void> {
  const response = await apiCall(`/api/nova/_ah/api/monitoring/v5/session/${sessionId}/close`, {
    method: 'POST',
    headers: novaProxyHeaders(novaAccountId),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to checkout session: ${error}`);
  }
}

/**
 * Add custom tags to a session
 */
export async function addSessionTags(
  sessionId: number,
  diaperId: number | null,
  tagger: string | null,
  tags: Array<{ name: string; value: string }>,
  novaAccountId?: string | null
): Promise<void> {
  const response = await apiCall('/api/nova/_ah/api/admin/v3/persistent-patient-tags', {
    method: 'POST',
    headers: novaProxyHeaders(novaAccountId),
    body: JSON.stringify({
      sessionId,
      diaperId,
      tagger,
      tags,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to add tags: ${error}`);
  }
}

/**
 * Toggle monitoring status for a session
 * Note: This might be controlled by criterion/policy, not a direct toggle
 */
export async function toggleMonitoringStatus(sessionId: number, monitored: boolean): Promise<void> {
  // This endpoint might not exist - monitoring is typically controlled by policy
  // Placeholder for future implementation
  throw new Error('Toggle monitoring not implemented - controlled by criterion/policy');
}
